import { DataGrid, DataGridColumn } from '@cimplico/components';
import { PlusIcon } from '@heroicons/react/20/solid';
import { useEffect, useRef, useState } from 'react';
import { ROW_SIZES } from '../../constants';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { JournalLinesFields, JournalTaxTypeEnum } from '../../types/journal';
import { WorksheetWithAccount } from '../../types/worksheet';
import { Account } from '../../types/account';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { WorkpaperSourceEnum } from '../../types/workpaper';
import NewOrEditJournalLineRow from './NewOrEditJournalLineRow';
import JournalTotals from './JournalTotals';
import { EntityAccount } from '../../types/entity';

const columns: DataGridColumn[] = [
    { alignment: 'center', width: '90%' },
    { alignment: 'left', width: '5%' },
];

type NewOrEditJournalTableProps = {
    entityId: string;
    worksheet?: WorksheetWithAccount;
    accounts?: EntityAccount[];
    narration?: string;
};

function NewOrEditJournalTable({ entityId, worksheet, accounts, narration }: NewOrEditJournalTableProps): JSX.Element {
    const { control, getValues, setValue } = useFormContext();
    const { append, remove } = useFieldArray({ control, name: 'lines' });
    const { provider } = useWorkpaperContext();

    useWatch({ name: ['debitTotal', 'creditTotal', 'debitSubtotal', 'creditSubtotal', 'debitTaxTotal', 'creditTaxTotal', 'valueOutBy'] });

    const [defaultDescription, setDefaultDescription] = useState(narration);

    const rowIdCounter = useRef<number>(1);
    const taxType = useWatch({ control, name: `taxType` });
    const showTaxRate = taxType && taxType !== JournalTaxTypeEnum.NO_TAX && provider === WorkpaperSourceEnum.XERO;

    function addRow(): void {
        append({
            id: `row-${rowIdCounter.current + 1}`,
            credit: null,
            debit: null,
            account: null,
            description: defaultDescription || null,
            amountEditable: true,
            taxRate: null,
        });
        rowIdCounter.current += 1;
    }

    function deleteRow(index: number): void {
        if (getValues('lines').length === 2) {
            return;
        }
        remove(index);
    }

    function reorderLines(sortedIds: string[]): void {
        const currentLines: JournalLinesFields[] = getValues('lines');
        const sortedLines = [...currentLines].sort((a, b) => sortedIds.indexOf(a.id) - sortedIds.indexOf(b.id));
        setValue('lines', sortedLines);
    }

    useEffect(() => {
        setDefaultDescription(narration);

        // Prefill descriptions for new empty lines
        const currentLines: JournalLinesFields[] = getValues('lines');
        for (const line of currentLines) {
            if (!line.description) {
                setValue(`lines.${currentLines.indexOf(line)}.description`, narration);
            }
        }
    }, [narration]);

    return (
        <div>
            <DataGrid
                columns={columns}
                rowSizes={ROW_SIZES}
                disablePagination
                totalRows={999}
                isSelectable={false}
                isDraggable
                onMove={reorderLines}
            >
                {getValues('lines')
                    .sort((a: JournalLinesFields, b: JournalLinesFields) =>
                        a.amountEditable === b.amountEditable ? 0 : !a.amountEditable ? 1 : -1
                    )
                    .map((line: JournalLinesFields, index: number) => {
                        return (
                            <NewOrEditJournalLineRow
                                key={line.id}
                                id={line.id as string}
                                index={index}
                                accounts={accounts}
                                amountEditable={line.amountEditable}
                                worksheet={worksheet}
                                entityId={entityId}
                                onDelete={deleteRow}
                                canRemove={getValues('lines').length > 2}
                            />
                        );
                    })}
            </DataGrid>
            <button
                className="border border-dashed rounded-lg flex items-center justify-center gap-2 border-indigo-600 text-xs font-bold text-indigo-600 mt-4 py-2 w-full"
                onClick={addRow}
            >
                <PlusIcon className="w-5 h-5" />
                Add line
            </button>
            <JournalTotals
                showTaxRate={showTaxRate}
                values={{
                    debitTotal: getValues('debitTotal'),
                    creditTotal: getValues('creditTotal'),
                    debitSubtotal: getValues('debitSubtotal'),
                    creditSubtotal: getValues('creditSubtotal'),
                    debitTaxTotal: getValues('debitTaxTotal'),
                    creditTaxTotal: getValues('creditTaxTotal'),
                    valueOutBy: getValues('valueOutBy'),
                }}
            />
        </div>
    );
}

export default NewOrEditJournalTable;
